import { inject, Injectable } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { sleep, TabletApp } from '@common';
import { TaskAnalysis } from '@shared/model/task.models';

export interface QuestionExplainOption {
  taskAnalysis?: TaskAnalysis;
  /**
   * 当前的任务id
   */
  currentTaskId?: number | string;
  /**
   * 初始化选择的题号
   */
  selectedQuestionNo?: string;
  /**
   * 任务未完成名单
   */
  taskUnfinishedStudent?: string[];
}

@Injectable()
export class QuestionExplainOverlayService {
  private overlayRef: OverlayRef;
  private initialing = false;

  overlay = inject(Overlay);
  modal = inject(NzMessageService);
  modalSrv = inject(NzModalService);

  async show(option: QuestionExplainOption) {
    if (this.initialing) return;

    try {
      document.documentElement.requestFullscreen();
    } catch (e) {
      console.log('不支持全屏能力');
    }

    await sleep(100);

    this.initialing = true;
    const overlayConfig = new OverlayConfig({
      hasBackdrop: true,
      backdropClass: 'question-explain-overlay-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      usePopover: false,
    });
    this.overlayRef = this.overlay.create(overlayConfig);
    // 这是一个延迟一秒出现的Loading，防止长时间加载大屏讲题模块导致页面看起来好像无响应的问题
    let loadingId: string;
    const timer = setTimeout(
      () => (loadingId = this.modal.loading('正在准备大屏讲题功能...', { nzDuration: 0 }).messageId),
      800
    );
    try {
      const { QuestionExplainComponent } = await import('./question-explain.component');
      const instance = this.overlayRef.attach(new ComponentPortal(QuestionExplainComponent));
      const lastLayoutDirection = localStorage.getItem('Q_EXPLAIN_LAYOUT_DIRECTION') === 'rtl';
      instance.instance.syncSrv.overlayRef = this;
      instance.instance.syncSrv.initOption = option;
      instance.instance.syncSrv.isRightLayout = lastLayoutDirection;
      await instance.instance.syncSrv.init();
      if (TabletApp.isTabletApp()) {
        this.modal.warning('大屏讲题转为教室大屏适配，平板电脑因系统各异，可能部分功能失效。');
      }
      clearTimeout(timer);
      if (loadingId) this.modal.remove(loadingId);
    } catch (e) {
      console.error(e);
      this.modal.error('大屏讲题功能加载失败，请刷新页面重试');
    } finally {
      this.initialing = false;
      clearTimeout(timer);
      if (loadingId) this.modal.remove(loadingId);
    }
  }

  /**
   * 关闭大屏讲题
   * closeAll 必须在 dispose 之前执行，确保所有 NzModal（如点名弹窗）先被关闭。
   * NzModal 挂载在全局 cdk-overlay-container 上，dispose 不会自动销毁它。
   */
  close() {
    this.modalSrv.closeAll();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      try {
        if (document?.fullscreenElement) document.exitFullscreen();
      } catch (e) {
        console.log('不支持全屏能力');
      }
    }
  }
}
